import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from '../context/HistoryContext';
import { useNavigate } from 'react-router-dom';
import { logOut, resetPassword } from '../services/auth';
import { deleteUser } from 'firebase/auth';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useDiagnosticContext } from '../context/DiagnosticContext';
import { 
  Plus, 
  Activity, 
  User, 
  Settings, 
  LogOut, 
  ChevronRight, 
  BarChart3, 
  ShieldCheck, 
  Clock,
  Circle,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  FileText,
  Trash2,
  ExternalLink,
  ChevronDown,
  KeyRound
} from 'lucide-react';
import styles from './Dashboard.module.css';

const getDisplayName = (user: any) => {
  if (user?.displayName) return user.displayName;

  const emailPrefix = user?.email?.split('@')[0];
  if (!emailPrefix) return 'User';

  return emailPrefix
    .replace(/[._-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

export function Dashboard() {
  const { user, profile } = useAuth();
  const { history, latestReport, deleteReport } = useHistory();
  const navigate = useNavigate();
  const { resetDiagnostic } = useDiagnosticContext();

  const displayName = getDisplayName(user);
  const hasPasswordProvider = !!user?.providerData?.some(p => p.providerId === 'password');
  const [resetState, setResetState] = useState<
    | { kind: 'idle' }
    | { kind: 'sending' }
    | { kind: 'success' }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  const handleResetPassword = async () => {
    if (!user?.email) return;
    if (!hasPasswordProvider) {
      setResetState({
        kind: 'error',
        message: 'Password reset is only available for email/password accounts.',
      });
      return;
    }
    setResetState({ kind: 'sending' });
    try {
      await resetPassword(user.email);
      setResetState({ kind: 'success' });
    } catch (err: any) {
      let message = 'Failed to send reset email.';
      if (err?.code === 'auth/invalid-email') message = 'Invalid email.';
      else if (err?.code === 'auth/too-many-requests') message = 'Too many requests.';
      else if (err?.code === 'auth/network-request-failed') message = 'Network error.';
      setResetState({ kind: 'error', message });
    }
  };

  const handleLogout = async () => {
    await logOut();
    navigate('/');
  };

  const handleNewDiagnostic = () => {
    resetDiagnostic();
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm("Are you sure you want to delete your account? This action cannot be undone.")) return;
    
    if (user) {
      try {
        await deleteUser(user);
        navigate('/');
      } catch (error: any) {
        if (error.code === 'auth/requires-recent-login') {
          alert("Please sign out and sign in again before deleting your account.");
        } else {
          alert(error.message);
        }
      }
    }
  };

  const calculateTrend = () => {
    if (history.length < 2) return null;
    const current = history[0].score;
    const previous = history[1].score;
    const diff = current - previous;
    
    if (diff > 0) return { label: `Health improved by ${diff}%`, type: 'up' };
    if (diff < 0) return { label: `Health declined by ${Math.abs(diff)}%`, type: 'down' };
    return { label: 'Health score stable', type: 'stable' };
  };

  const trend = calculateTrend();

  const getStatusBadge = (score: number) => {
    if (score >= 85) return { label: 'Healthy', color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' };
    if (score >= 60) return { label: 'Warning', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' };
    return { label: 'Critical', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' };
  };

  return (
    <div className="app">
      <Header />
      <main className={styles.dashboard}>
        <div className="wrap">
          <header className={styles.dashHeader}>
            <div className={styles.welcome}>
              <h1>Hello, {displayName.split(' ')[0]}</h1>
              <p>Manage your system diagnostics and health reports.</p>
            </div>
            <button className={styles.newReportBtn} onClick={handleNewDiagnostic}>
              <Plus size={18} />
              Start New Diagnostic
            </button>
          </header>

          <div className={styles.statsRow}>
            <div className={styles.statCard}>
              <div className={styles.statIcon}><Activity size={20} /></div>
              <div className={styles.statContent}>
                <span className={styles.statLabel}>Diagnostics Run</span>
                <span className={styles.statValue}>{history.length}</span>
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statIcon}><ShieldCheck size={20} /></div>
              <div className={styles.statContent}>
                <span className={styles.statLabel}>Issues Found</span>
                <span className={styles.statValue}>{latestReport?.findingsCount || 0}</span>
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statIcon}><BarChart3 size={20} /></div>
              <div className={styles.statContent}>
                <span className={styles.statLabel}>Latest Health Score</span>
                <span className={styles.statValue}>{latestReport?.score || '--'}%</span>
              </div>
            </div>
          </div>

          {trend && (
            <div className={`${styles.trendBar} ${styles[trend.type]}`}>
              {trend.type === 'up' ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
              <span>{trend.label}</span>
            </div>
          )}

          <div className={styles.dashGrid}>
            <section className={styles.mainContent}>
              <div className={styles.sectionHeader}>
                <h2>Diagnostic History</h2>
              </div>
              
              {history.length === 0 ? (
                <div className={styles.emptyState}>
                  <div className={styles.placeholderIcon}><Clock size={32} /></div>
                  <h3>Your diagnostic history will appear here.</h3>
                  <p>Run your first scan to get detailed insights into your system performance.</p>
                  <button onClick={handleNewDiagnostic}>Start New Diagnostic</button>
                </div>
              ) : (
                <div className={styles.historyList}>
                  {history.map((report) => {
                    const status = getStatusBadge(report.score);
                    return (
                      <div key={report.id} className={styles.reportCard}>
                        <div className={styles.reportMain}>
                          <div className={styles.reportScoreBox}>
                            <span className={styles.reportScore}>{report.score}%</span>
                            <span className={styles.reportLabel}>Score</span>
                          </div>
                          <div className={styles.reportDetails}>
                            <div className={styles.reportHeaderRow}>
                              <span className={styles.reportDate}>{new Date(report.timestamp).toLocaleString()}</span>
                              <span className={styles.statusBadge} style={{ color: status.color, background: status.bg }}>
                                {status.label}
                              </span>
                            </div>
                            <h3>{report.category}</h3>
                            <p className={styles.reportMeta}>
                              <AlertCircle size={14} /> {report.findingsCount} findings detected • {report.manufacturer} PC
                            </p>
                          </div>
                        </div>
                        <div className={styles.reportActions}>
                          <button className={styles.viewBtn} onClick={() => navigate('/report')}>
                            View Full Report <ChevronRight size={16} />
                          </button>
                          <div className={styles.subActions}>
                            <button className={styles.iconBtn} title="Download PDF"><FileText size={16} /></button>
                            <button className={styles.iconBtn} onClick={() => deleteReport(report.id)} title="Delete"><Trash2 size={16} /></button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <aside className={styles.sidebar}>
              <div className={styles.sidebarCard}>
                <div className={styles.sectionHeader}>
                  <h2>Support Actions</h2>
                </div>
                <div className={styles.actionLinks}>
                  {profile && (profile.role === 'admin' || profile.role === 'super_admin') && (
                    <button onClick={() => navigate('/admin')} style={{ background: 'var(--accent)', color: '#fff', border: 'none' }}>
                      <ShieldCheck size={16} /> Open Admin Panel
                    </button>
                  )}
                  <button onClick={handleNewDiagnostic}>
                    <RefreshCw size={16} /> Run Follow-up Diagnostic
                  </button>
                  <button>
                    <Settings size={16} /> Account Settings
                  </button>
                </div>
              </div>

              <div className={styles.proCard}>
                <div className={styles.proHeader}>
                  <div className={styles.proBadge}>
                    {profile?.plan === 'family' ? 'FAMILY' : 'CREDITS'}
                  </div>
                  <h3>Report Credits</h3>
                </div>
                <div style={{ marginTop: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span className={styles.proLabel}>Reports Used:</span>
                    <span className={styles.proValue}>{profile?.reports_used || 0}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <span className={styles.proLabel}>Credits Remaining:</span>
                    <span className={`${styles.proValue} ${(profile?.credits || 0) <= 0 ? styles.warning : ''}`}>
                      {profile?.credits || 0}
                    </span>
                  </div>
                  
                  {(profile?.credits || 0) <= 0 ? (
                    <button className={styles.proBtn} onClick={() => navigate('/pricing')}>
                      Buy Report Credits <ChevronRight size={16} />
                    </button>
                  ) : (
                    <button className={styles.proBtn} onClick={handleNewDiagnostic} style={{ background: 'var(--accent)', color: '#000', border: 'none' }}>
                      Run Full AI Diagnostic <ChevronRight size={16} />
                    </button>
                  )}
                </div>
              </div>


              <div className={styles.accountCard}>
                <div className={styles.profileInfo}>
                  {user?.photoURL ? (
                    <img src={user.photoURL} alt="Profile" onError={(e) => e.currentTarget.style.display = "none"} />
                  ) : (
                    <div className={styles.avatarFallback}>{displayName.charAt(0)}</div>
                  )}
                  <div>
                    <h3>{displayName}</h3>
                    <p>{user?.email}</p>
                  </div>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={handleResetPassword}
                    disabled={resetState.kind === 'sending'}
                    title={hasPasswordProvider ? 'Send a password reset email' : 'Password reset is only available for email/password accounts'}
                    style={{
                      width: '100%',
                      background: 'rgba(16, 185, 129, 0.1)',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      color: '#10b981',
                      padding: '10px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      cursor: resetState.kind === 'sending' ? 'wait' : 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseOver={(e) => {
                      if (resetState.kind !== 'sending') {
                        e.currentTarget.style.background = 'rgba(16, 185, 129, 0.15)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (resetState.kind !== 'sending') {
                        e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)';
                      }
                    }}
                  >
                    <KeyRound size={16} /> {resetState.kind === 'sending' ? 'Sending...' : 'Reset Password'}
                  </button>

                  {resetState.kind === 'success' && (
                    <div style={{ fontSize: '0.75rem', color: '#10b981', textAlign: 'center', background: 'rgba(16, 185, 129, 0.05)', padding: '6px', borderRadius: '6px' }}>
                      Reset email sent.
                    </div>
                  )}
                  {resetState.kind === 'error' && (
                    <div style={{ fontSize: '0.75rem', color: '#ef4444', textAlign: 'center', background: 'rgba(239, 68, 68, 0.05)', padding: '6px', borderRadius: '6px' }}>
                      {resetState.message}
                    </div>
                  )}

                  <button onClick={handleLogout} className={styles.logoutBtn} style={{ marginBottom: 0 }}>
                    <LogOut size={16} /> Sign Out
                  </button>
                  <button onClick={handleDeleteAccount} className={styles.deleteBtn}>
                    <Trash2 size={16} /> Delete Account
                  </button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

const RefreshCw = ({ size }: { size: number }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
    <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
    <path d="M16 16h5v5" />
  </svg>
);
